import { FILE_DATE_FILTERS, NO_FILE_FILTERS, type FileDateFilter, type FileFilterState } from './file-browser';
import { FILE_TYPE_KINDS, type FileTypeKind } from './artifact-image';

// The Files browser's state, in the URL.
//
// A filter you set and then lose on refresh is worse than no filter: you have already read the screen once and
// have to rebuild the question from memory. Putting it in the query string also makes the view **shareable** —
// "the PDFs Melody made last week" is a link — and gives Back its ordinary meaning inside the browser.
//
// The category redesign (`lib/use-category-redesign.ts`) writes only the folder (`{ filters: false }`): a
// link may still open the list narrowed, but a pick the reader makes is never written back, so a refresh or
// a return starts unfiltered — the rule every category page follows (`lib/use-session-space-filter.ts`).
//
// Encoding rules, all in service of a URL a person can read:
//  - only non-default values appear, so an unfiltered list has a clean URL;
//  - the params are short words (`space`, `by`, `when`, `kind`, `folder`) rather than a serialised blob;
//  - a value we don't recognise is DROPPED, not an error — a hand-edited or stale link degrades to the
//    unfiltered list instead of rendering an empty screen or throwing.
//
// `by` carries the creator PAIR (type and name) because that pair is the identity — two sub-agents with
// different names are two different writers. `~` separates them: it survives a URL unescaped and cannot appear
// in an actor type.

/** The two ways the Files list can be drawn. An array as well as a type, because the stored per-device
 *  preference has to validate a string it did not write (`lib/files-view-pref.ts`).
 *
 *  **The view is deliberately NOT part of the URL.** It is a preference, not part of the question you asked of
 *  your files — a link should carry where you are and what you narrowed to, not how the person who sent it likes
 *  to look at things. It lives in `localStorage` instead, which is where both phones keep it. */
export const FILE_VIEW_MODES = ['list', 'thumbnails'] as const;
export type FilesViewMode = (typeof FILE_VIEW_MODES)[number];

export interface FilesUrlState {
  folder: string | null;
  filters: FileFilterState;
}

/** The separator between a creator's type and its name in the `by` param. */
const CREATOR_SEPARATOR = '~';

/** A single query value, as Next hands it over (a repeated param arrives as an array). */
type QueryValue = string | string[] | undefined;

const first = (value: QueryValue): string | null => {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
};

/** Reads the browser state out of a Next query object. Unknown values fall back to the default. */
export function readFilesUrlState(query: Record<string, QueryValue>): FilesUrlState {
  const by = first(query.by);
  const when = first(query.when);
  const kind = first(query.kind);
  const separator = by?.indexOf(CREATOR_SEPARATOR) ?? -1;

  return {
    folder: first(query.folder),
    filters: {
      workspaceId: first(query.space),
      folder: first(query.scope),
      creator: by ? { type: separator < 0 ? by : by.slice(0, separator), name: separator < 0 ? '' : by.slice(separator + 1) } : null,
      date: isDateFilter(when) ? when : NO_FILE_FILTERS.date,
      type: isTypeKind(kind) ? kind : null,
    },
  };
}

/** The query params for `state` — every default encoded as `undefined`, i.e. absent from the URL. */
export function writeFilesUrlState(state: FilesUrlState, { filters = true }: FilesUrlWrite = {}): Record<string, string | undefined> {
  if (!filters) {
    return { folder: state.folder || undefined, space: undefined, scope: undefined, by: undefined, when: undefined, kind: undefined };
  }
  const { workspaceId, folder, creator, date, type } = state.filters;
  return {
    folder: state.folder || undefined,
    space: workspaceId ?? undefined,
    // The subtree scope (an agent's folder). Absent means the whole tree — which is exactly what
    // dismissing the chip should survive a reload as.
    scope: folder ?? undefined,
    by: creator ? `${creator.type}${CREATOR_SEPARATOR}${creator.name}` : undefined,
    when: date === 'any' ? undefined : date,
    kind: type ?? undefined,
  };
}

/** `filters: false` writes the folder only and clears every filter param. */
export interface FilesUrlWrite {
  filters?: boolean;
}

/** Merges the browser's params into an existing query, dropping the ones that are now at their default.
 *
 *  Merging rather than replacing matters: these pages carry other params of their own (`?file=` opens the
 *  viewer, `?slug=` is the route), and a write that replaced the query would drop them. */
export function mergeFilesUrlState(query: Record<string, QueryValue>, state: FilesUrlState, write: FilesUrlWrite = {}): Record<string, QueryValue> {
  const next: Record<string, QueryValue> = { ...query };
  for (const [key, value] of Object.entries(writeFilesUrlState(state, write))) {
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return next;
}

/** True when the two states would produce the same URL — the guard that stops a sync effect from looping. */
export function sameFilesUrlState(a: FilesUrlState, b: FilesUrlState, write: FilesUrlWrite = {}): boolean {
  const left = writeFilesUrlState(a, write);
  const right = writeFilesUrlState(b, write);
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => left[key] === right[key]);
}

function isDateFilter(value: string | null): value is FileDateFilter {
  return value !== null && (FILE_DATE_FILTERS as readonly string[]).includes(value);
}

function isTypeKind(value: string | null): value is FileTypeKind {
  return value !== null && (FILE_TYPE_KINDS as readonly string[]).includes(value);
}
